import './DatePickerButton.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { Dispatch, Ref, SetStateAction } from 'react';
import { BiCaretDown } from 'react-icons/bi';
import { MdCalendarToday } from 'react-icons/md';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { Button } from '../../Button';
import { IconButton } from '../../IconButton/IconButton';
import { InteractiveSpan } from '../../InteractiveEl';
import {
  Appearance,
  AppearanceType,
  Background,
  BackgroundType,
  Design,
  DesignType,
  Placement,
  Size,
  SizeType,
  StretchMode,
  StretchModeType
} from '../../designConstants';
import { DateRange } from '../date-picker-types';

const PRETTY_DATE_FORMAT = 'M/d/yy';
const PRETTY_MONTH_FORMAT = 'MMM yyyy';

const makePrettyDate = (date?: string) =>
  date ? Dfns.format(PRETTY_DATE_FORMAT)(Dfns.parseISO(date)) : '___';

const makePrettyMonth = (date?: string) =>
  date ? Dfns.format(PRETTY_MONTH_FORMAT)(Dfns.parseISO(date)) : '___';

interface DatePickerButtonProps {
  reference?: Ref<HTMLButtonElement>;
  setShow?: (show: boolean, save?: boolean) => void;
  range?: DateRange;
  fullWeeksOnly?: boolean;
  exclusiveEndWeek?: boolean;
  fullMonthsOnly?: boolean;
  setFocus?: Dispatch<SetStateAction<'START' | 'END'>>;
  className?: string;
  comparison?: boolean;
  comparisonRange?: DateRange;
  background?: BackgroundType;
  appearance?: AppearanceType;
  design?: DesignType;
  disabled?: boolean;
  hasError?: boolean;
  /** Whether the picker popover is open; flips the caret like an open Dropdown. */
  isOpen?: boolean;
  stretch?: StretchModeType;
  label?: string;
  size?: SizeType;
  isSingle?: boolean;
  placeholder?: string;
  /**
   * Collapse to the bare calendar icon while `disabled`, with the range moved to a hover tooltip.
   * OPT-IN, and deliberately not driven by `disabled` alone: most disabled pickers in the app are
   * read-only form fields (a rule's timeframe, a locked analysis window) whose value has to stay on
   * screen. Only a picker that is disabled because something ELSE now owns the date -- a dashboard
   * card following the dashboard-wide picker -- has nothing worth the horizontal space.
   */
  iconOnlyWhenLinked?: boolean;
  /**
   * Collapse to the bare calendar icon whatever the `disabled` state, range moved to the same hover
   * tooltip. Unlike `iconOnlyWhenLinked` the button stays live and opens the picker on click -- for
   * callers that are short on horizontal space, not ones whose date is owned elsewhere.
   */
  iconOnly?: boolean;
  /**
   * Extra class on the collapsed state's hover tooltip, for callers that need its surface to match
   * neighbouring tooltips (e.g. dashboarding's `chart-tooltip`). Mirrors `InfoTooltip`'s
   * `popoverClassName`; kept a prop rather than hardcoded so this shared component owes nothing to
   * any one page's styling.
   */
  tooltipClassName?: string;
}

export const DatePickerButton = ({
  reference,
  setShow,
  fullWeeksOnly,
  exclusiveEndWeek,
  fullMonthsOnly = false,
  range,
  setFocus,
  className,
  comparison = false,
  comparisonRange,
  background = Background.LIGHT,
  disabled = false,
  hasError = false,
  isOpen = false,
  stretch = StretchMode.FIT,
  appearance = Appearance.FILLED,
  design = Design.BRAND,
  label,
  size = Size.MD,
  isSingle = false,
  placeholder,
  iconOnlyWhenLinked = false,
  iconOnly = false,
  tooltipClassName
}: DatePickerButtonProps) => {
  const { start, end } = range || {};
  const { start: comparisonStart, end: comparisonEnd } = comparisonRange || {};

  const formatRangeDate = (date?: string, isEnd = false) => {
    if (fullMonthsOnly) return makePrettyMonth(date);
    const weekPrefix = fullWeeksOnly && !(isEnd && exclusiveEndWeek) ? 'Week of ' : '';
    return `${weekPrefix}${makePrettyDate(date)}`;
  };

  const singleDateDisplay = isSingle && (
    <div className='single-date-display'>
      {label && <span>{`${label}: `}</span>}
      {start ? (
        <span className='single-date'>{formatRangeDate(start)}</span>
      ) : (
        <span>{placeholder}</span>
      )}
    </div>
  );

  const dateRangeDisplay =
    range == null ? (
      <div>
        <span>{placeholder}</span>
      </div>
    ) : (
      singleDateDisplay || (
        <div>
          {label && <span>{`${label}: `}</span>}
          <span className='single-date'>{formatRangeDate(start)}</span> –{' '}
          <InteractiveSpan
            className='single-date'
            disabled={disabled}
            onClick={() => (setFocus ? setFocus('END') : null)}
          >
            {formatRangeDate(end, true)}
          </InteractiveSpan>
          {comparison && (
            <span className='comparison-period'>
              <span> vs </span>
              <span className='single-date'>{formatRangeDate(comparisonStart)}</span> –{' '}
              <InteractiveSpan
                className='single-date'
                disabled={disabled}
                onClick={() => (setFocus ? setFocus('END') : null)}
              >
                {formatRangeDate(comparisonEnd)}
              </InteractiveSpan>
            </span>
          )}
        </div>
      )
    );

  if (iconOnly || (disabled && iconOnlyWhenLinked)) {
    const rangeLabel = [
      range ? `${formatRangeDate(start)} \u2013 ${formatRangeDate(end, true)}` : placeholder,
      comparison && comparisonRange
        ? `vs ${formatRangeDate(comparisonStart)} \u2013 ${formatRangeDate(comparisonEnd)}`
        : undefined
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement={Placement.BOTTOM}
        overlay={
          <Popover className={cn('info-tooltip-popover', tooltipClassName)}>
            <Popover.Body>{rangeLabel}</Popover.Body>
          </Popover>
        }
      >
        {/* A disabled button fires no hover events, so the trigger has to sit on a wrapper -- the
            same reason DashboardCard wraps its own disabled link button. `reference` still goes on
            the button itself: the picker's popup positions against it. */}
        <span className='date-picker-button-icon-only'>
          <IconButton
            aria-label={rangeLabel}
            ref={reference}
            onClick={() => (setShow ? setShow(true) : null)}
            disabled={disabled}
            background={background}
            appearance={appearance}
            design={design}
            size={size}
            className={className}
          >
            <MdCalendarToday />
          </IconButton>
        </span>
      </OverlayTrigger>
    );
  }

  return (
    <Button
      background={background}
      ref={reference}
      className={cn('date-picker-button', className, stretch, {
        'full-weeks-only': fullWeeksOnly,
        'has-error': hasError
      })}
      onClick={() => (setShow ? setShow(true) : null)}
      leadingElement={<MdCalendarToday />}
      disabled={disabled}
      aria-expanded={isOpen}
      appearance={appearance}
      design={design}
      size={size}
    >
      <div className='dp-range-and-caret'>
        <div className='dpb-range'>{dateRangeDisplay}</div>
        <BiCaretDown className='caret-icon' />
      </div>
    </Button>
  );
};
